import { requestAICompletion } from '../../../../api'
import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../../../constants/common'
import { isAIFinanceAccessRevokedError } from '../../../../utils/aiAgents'
import {
  isApiConflictStatus,
  isApiServerInternalError,
} from '../../../../utils/api'
import { GretaAIChatStatus } from '../../GretaAIChat.constants'
import {
  getGretaAIChatErrorStatus,
  getIsGretaAIChatTimeout,
} from '../../GretaAIChat.utils'

import { GRETA_AI_CHAT_REPLY_TIMEOUT_MS } from './useGretaAIChatReplyRequest.constants'
import {
  getGretaAIChatCompletionMessages,
  getGretaAIChatConflictRetryMessages,
  getGretaAIChatLastQuestion,
} from './useGretaAIChatReplyRequest.utils'
import { useGretaAIChatReplyResponse } from './useGretaAIChatReplyResponse'

// Each step returns the turn's outcome (defaults in `finishReplyTurn`) instead of mutating flags.
export function useGretaAIChatReplyRequest({
  cancelServerTurn,
  checkGenerationSettled,
  clearTurn,
  endReplySettle,
  enterGenerationWait,
  fetchMessages,
  forgetDeliveredTurn,
  getIsOpenConversation,
  getIsStopPending,
  markTurnTimedOut,
  reloadMessages,
  setMessages,
  setStatus,
  settleGenerationWait,
  startReplySettle,
  startTurn,
  syncDeliveredTurn,
  trackDeliveredTurn,
  turnControllersRef,
  onGenerationSettled = EMPTY_FUNCTION,
  onSourceRevoked = EMPTY_FUNCTION,
  onTurnSettled = EMPTY_FUNCTION,
  onTurnStart = EMPTY_FUNCTION,
}) {
  function beginReplyTurn(conversationId) {
    forgetDeliveredTurn(conversationId)
    onTurnStart(conversationId)
    startTurn(conversationId)

    if (getIsOpenConversation(conversationId)) {
      setStatus(GretaAIChatStatus.Thinking)
    }

    const controller = new AbortController()

    turnControllersRef.current.set(conversationId, controller)

    return controller
  }

  // Skipped once a newer turn has claimed this slot — its cleanup and reporting are its own.
  function finishReplyTurn({
    controller,
    conversationId,
    delivered = false,
    ownsTurn = true,
    shouldClearTurnStart = true,
  }) {
    const turnController = turnControllersRef.current.get(conversationId)

    if (ownsTurn && (!turnController || turnController === controller)) {
      turnControllersRef.current.delete(conversationId)
      endReplySettle(conversationId)

      if (shouldClearTurnStart) {
        clearTurn(conversationId)
      }

      onTurnSettled(conversationId, {
        delivered,
        isBackground: !getIsOpenConversation(conversationId),
      })
    }
  }

  // The backend still runs this turn — wait it out, then reload what it saved.
  async function waitForRunningTurn(conversationId, options) {
    await enterGenerationWait(conversationId, options)

    turnControllersRef.current.delete(conversationId)

    return settleGenerationWait(conversationId)
  }

  const { handleReply } = useGretaAIChatReplyResponse({
    checkGenerationSettled,
    endReplySettle,
    getIsOpenConversation,
    setMessages,
    setStatus,
    startReplySettle,
    syncDeliveredTurn,
    trackDeliveredTurn,
    waitForRunningTurn,
    onGenerationSettled,
  })

  // 409: another answer is already running — wait for it, then actually send
  // this question instead of losing it.
  async function retryAfterConflict(conversationId, payloadMessages) {
    const reloadedMessages =
      (await waitForRunningTurn(conversationId)) ??
      (await fetchMessages(conversationId))

    if (getIsStopPending(conversationId)) {
      return { shouldClearTurnStart: false }
    }

    const retryMessages = getGretaAIChatConflictRetryMessages(
      reloadedMessages,
      payloadMessages
    )

    if (getIsOpenConversation(conversationId)) {
      setMessages(retryMessages)
    }

    await requestReply(conversationId, retryMessages)

    return { ownsTurn: false }
  }

  async function reloadAfterServerError(conversationId) {
    turnControllersRef.current.delete(conversationId)

    if (getIsOpenConversation(conversationId)) {
      await reloadMessages(conversationId)
    }

    return EMPTY_OBJECT
  }

  async function handleFailedReply({
    controller,
    conversationId,
    payloadMessages,
    reason,
  }) {
    // Also for a chat the user has left: the refusal holds for every Finance chat.
    if (isAIFinanceAccessRevokedError(reason)) {
      onSourceRevoked()
    }

    const errorStatus = await getGretaAIChatErrorStatus(reason)

    if (turnControllersRef.current.get(conversationId) !== controller) {
      return { ownsTurn: false }
    }

    turnControllersRef.current.delete(conversationId)

    if (getIsStopPending(conversationId)) {
      return { shouldClearTurnStart: false }
    }

    const isTimeout = getIsGretaAIChatTimeout(errorStatus)

    if (isTimeout) {
      markTurnTimedOut(
        conversationId,
        getGretaAIChatLastQuestion(payloadMessages)
      )
      cancelServerTurn(conversationId, controller, { isTimeout: true })
    }

    if (getIsOpenConversation(conversationId)) {
      setStatus(errorStatus)
    }

    return { shouldClearTurnStart: !isTimeout }
  }

  function handleReplyError({
    controller,
    conversationId,
    payloadMessages,
    reason,
  }) {
    // A manual Stop aborts the request — that is a user action, not a failure;
    // the parallel stop confirmation it triggers owns settlement from here.
    if (controller.signal.aborted) {
      return { shouldClearTurnStart: false }
    }

    const httpStatus = reason?.response?.status

    if (isApiConflictStatus(httpStatus)) {
      return retryAfterConflict(conversationId, payloadMessages)
    }

    if (isApiServerInternalError(httpStatus)) {
      return reloadAfterServerError(conversationId)
    }

    return handleFailedReply({
      controller,
      conversationId,
      payloadMessages,
      reason,
    })
  }

  async function requestReply(conversationId, payloadMessages) {
    const controller = beginReplyTurn(conversationId)

    const outcome = await requestAICompletion({
      conversationId,
      messages: getGretaAIChatCompletionMessages(payloadMessages),
      options: {
        signal: controller.signal,
        timeout: GRETA_AI_CHAT_REPLY_TIMEOUT_MS,
      },
    })
      .then(response =>
        handleReply({ conversationId, payloadMessages, response })
      )
      .catch(reason =>
        handleReplyError({
          controller,
          conversationId,
          payloadMessages,
          reason,
        })
      )

    finishReplyTurn({ ...outcome, controller, conversationId })
  }

  return { requestReply }
}
